// Country configuration for GeoDash Platform
// Each country defines its branding, labels, infrastructure types, map defaults, and workflows.
// This file is the single source of truth for multi-country adaptation.

export const COUNTRIES = {
  quebec: {
    code: 'quebec',
    name: 'Québec',
    flag: 'maple-leaf',
    brand: 'GeoQC',
    tagline: 'Plateforme de gestion des infrastructures municipales',
    palette: {
      // Quebec blue palette (matches existing prototype)
      primary: '#002855',
      primaryHover: '#0057A8',
      accent: '#1976D2',
      light: '#90CAF9',
      surface: '#E3F2FD',
      success: '#1565C0',
      warning: '#F4A261',
      danger: '#C1121F',
    },
    map: {
      defaultCenter: [46.8, -71.2],
      defaultZoom: 7,
      cityCenter: [45.5017, -73.5673],
      cityZoom: 11,
      cityName: 'Montréal',
    },
    labels: {
      // Quebec-specific terminology
      region: 'Région administrative',
      regions: 'Régions',
      commune: 'Municipalité',
      communes: 'Municipalités',
      department: 'MRC',
      organization: 'Organisme',
      currency: 'CAD',
      locale: 'fr-CA',
    },
    infrastructureTypes: [
      { code: 'EAU', name: 'Eau potable', icon: '💧', color: '#1976D2' },
      { code: 'ELEC', name: 'Hydro-Québec', icon: '⚡', color: '#F4A261' },
      { code: 'ROUTE', name: 'Voirie', icon: '🛣️', color: '#6b7c6e' },
      { code: 'SANTE', name: 'CIUSSS/CISSS', icon: '🏥', color: '#C1121F' },
      { code: 'ECOLE', name: 'Éducation', icon: '🏫', color: '#9C27B0' },
      { code: 'AUTRE', name: 'Autre', icon: '🔧', color: '#607D8B' },
    ],
    nationalAgencies: ['Hydro-Québec', 'MTQ', 'Société québécoise des infrastructures'],
  },
  senegal: {
    code: 'senegal',
    name: 'Sénégal',
    flag: 'senegal',
    brand: 'GeoDash Sénégal',
    tagline: "Plateforme d'intelligence territoriale et d'opérations d'infrastructures",
    palette: {
      // Senegal green/gold/red palette
      primary: '#00853F',
      primaryHover: '#006B33',
      accent: '#FDEF42',
      light: '#A6D9A8',
      surface: '#E8F5E9',
      success: '#00853F',
      warning: '#FDEF42',
      danger: '#E31B23',
    },
    map: {
      defaultCenter: [14.4974, -14.4524], // Sénégal centroid
      defaultZoom: 7,
      cityCenter: [14.7167, -17.4677], // Dakar
      cityZoom: 11,
      cityName: 'Dakar',
    },
    labels: {
      region: 'Région',
      regions: 'Régions',
      commune: 'Commune',
      communes: 'Communes',
      department: 'Département',
      organization: 'Agence nationale',
      currency: 'XOF',
      locale: 'fr-SN',
    },
    infrastructureTypes: [
      { code: 'EAU', name: 'SONES / Eau', icon: '💧', color: '#1976D2' },
      { code: 'ELEC', name: 'SENELEC', icon: '⚡', color: '#F4A261' },
      { code: 'ASSAIN', name: 'ONAS / Assainissement', icon: '🚰', color: '#00ACC1' },
      { code: 'ROUTE', name: 'AGEROUTE', icon: '🛣️', color: '#6b7c6e' },
      { code: 'SANTE', name: 'Postes de santé', icon: '🏥', color: '#C1121F' },
      { code: 'ECOLE', name: 'Éducation', icon: '🏫', color: '#9C27B0' },
      { code: 'AUTRE', name: 'Autre', icon: '🔧', color: '#607D8B' },
    ],
    nationalAgencies: ['SENELEC', 'SONES', 'ONAS', 'AGEROUTE', 'ADM'],
  },
};

export const DEFAULT_COUNTRY = 'quebec';

export function getCountry(code) {
  return COUNTRIES[code] || COUNTRIES[DEFAULT_COUNTRY];
}

// Common across all countries
export const ROLES = {
  superadmin: { label: 'Super-administrateur', rank: 100 },
  admin_region: { label: 'Admin régional', rank: 70 },
  admin_commune: { label: 'Admin commune', rank: 50 },
  agent: { label: 'Agent terrain', rank: 30 },
  viewer: { label: 'Lecteur', rank: 10 },
};

export const SIGNAL_PRIORITIES = [
  { value: 'critique', label: 'Critique', color: '#C1121F' },
  { value: 'haute', label: 'Haute', color: '#F4A261' },
  { value: 'normale', label: 'Normale', color: '#1565C0' },
  { value: 'basse', label: 'Basse', color: '#40916C' },
];

export const SIGNAL_STATUSES = [
  { value: 'a_valider', label: 'À valider', color: '#7E57C2' },
  { value: 'nouveau', label: 'Nouveau', color: '#C62828' },
  { value: 'en_cours', label: 'En cours', color: '#E65100' },
  { value: 'resolu', label: 'Résolu', color: '#40916C' },
  { value: 'rejete', label: 'Rejeté', color: '#9e9e9e' },
];

export const INFRA_STATUSES = [
  { value: 'operationnel', label: 'Opérationnel', color: '#1565C0' },
  { value: 'degrade', label: 'Dégradé', color: '#F4A261' },
  { value: 'hors_service', label: 'Hors service', color: '#C1121F' },
  { value: 'en_construction', label: 'En construction', color: '#9C27B0' },
  { value: 'inconnu', label: 'Inconnu', color: '#9e9e9e' },
];

export function scoreColor(s) {
  if (s === null || s === undefined) return '#ccc';
  if (s >= 75) return '#1565C0';
  if (s >= 55) return '#F4A261';
  return '#C1121F';
}

export function scoreClass(s) {
  if (s >= 75) return 'score-good';
  if (s >= 55) return 'score-warn';
  return 'score-bad';
}
